'use client';

import { observer } from 'mobx-react';
import { getStore } from '~/mobx/AppStore';
import { CustomerData } from '~/types/memberCustomerDirectorySession';
import { Options } from '~/definitions/ballot';
import SlagList from '../SlagList/SlagList';
import Loading from '../Loading/Loading';
import CandidateList from '../CandidatePage/CandidateList/CandidateList';
import VoteSummary from '../CandidatePage/VoteSummary/VoteSummary';
import ElectionInfo from '../ElectionInfo/ElectionInfo';
import Intro from './Intro/Intro';
import SubmitVoteMessage from '../SubmitVoteMessage';
import { getHeadingAndDesc } from '~/utils/getHeadingAndDesc';
import WarningMessage from '../WarningMessage';
import { useEffect } from 'react';
import { BODY_OVERFLOW_HIDDEN_CLASS } from '~/const/ballot';

type Props = {
    coopId: string;
    customer?: CustomerData['customer'];
    slagList?: Options[];
    isDisabledVote?: boolean;
    heading: string;
    description?: string;
    selectedElectionId?: string;
    selectedSlagId?: string;
    selectedRegionId?: string;
    isUpdatingSlag?: boolean;
    setSelectedSlagId: (value: string) => void;
    onSlagChange: (slagId: string) => Promise<void>;
    isShownCandidates: boolean;
    hideSlagList: boolean;
    voteStatus: string;
    isUserAlreadyVoted?: boolean;
    isUserChild?: boolean;
    isOpenVoteSummaryModal?: boolean;
    electionLogoUrl: string;
};

const Main = (props: Readonly<Props>) => {
    const {
        slagList = [],
        isDisabledVote,
        selectedSlagId,
        isUpdatingSlag,
        setSelectedSlagId,
        onSlagChange,
        isShownCandidates,
        hideSlagList,
        voteStatus,
        heading,
        description,
        isUserAlreadyVoted,
        isUserChild,
        isOpenVoteSummaryModal,
        electionLogoUrl,
    } = props;

    const baseStyles = 'relative transition-all duration-1000 ease-in-out';
    const hiddenStyles = 'h-0 opacity-0 z-0 overflow-hidden';
    const visibleStyles = 'h-auto opacity-100 z-10 overflow-visible';

    const introStyles = `${baseStyles} ${isShownCandidates ? hiddenStyles : visibleStyles}`;
    const candidatePageStyles = `${baseStyles} ${isShownCandidates ? visibleStyles : hiddenStyles}`;

    const handleChangeSlag = (value: string) => {
        setSelectedSlagId(value);
        onSlagChange(value);
    };

    return voteStatus !== '' ? (
        <SubmitVoteMessage />
    ) : (
        <div className="ballot-page pb-[100px] md:pb-[116px]">
            <div className="container-fluid mx-[15px]">
                {!hideSlagList && (
                    <SlagList
                        id={'change-membership'}
                        name={'changeMembership'}
                        onChange={handleChangeSlag}
                        value={selectedSlagId ?? ''}
                        options={slagList}
                        testId={'change-membership'}
                    />
                )}
                {!isShownCandidates && (
                    <div className={introStyles}>
                        <Intro />
                    </div>
                )}
                {isShownCandidates && (
                    <div className={candidatePageStyles}>
                        <div className="candidate">
                            <ElectionInfo
                                heading={heading}
                                description={description}
                                electionLogoUrl={electionLogoUrl}
                            />
                            <WarningMessage
                                isDisabledVote={isDisabledVote}
                                isUserAlreadyVoted={isUserAlreadyVoted}
                                isUserChild={isUserChild}
                                currentPage={'ballot'}
                            />
                            <CandidateList />
                            <VoteSummary />
                            {isOpenVoteSummaryModal && <div className="backdrop-vote-summary" />}
                        </div>
                    </div>
                )}
            </div>
            {isUpdatingSlag && <Loading />}
        </div>
    );
};

const MainWithStore = observer(() => {
    const { ballotStore, candidateStore, voteStore, previewStore } = getStore();
    const coopId = ballotStore.coopId;
    const customer = ballotStore.customers?.customer;
    const slagList = ballotStore.getBallotSlagList();
    const { heading, description } = getHeadingAndDesc(false);
    const isDisabledVote = candidateStore.isDisabledVote;
    const selectedElectionId = ballotStore.selectedElectionId;
    const selectedSlagId = ballotStore.selectedSlagId;
    const selectedRegionId = ballotStore.selectedRegionId;
    const voteStatus = voteStore.voteStatus;

    const isStandAdmin = ballotStore.getIsStandAdmin();
    const isShownCandidates = ballotStore.isShownCandidates || isStandAdmin;
    const isPreview = previewStore.isInitialized;
    const isOpenVoteSummaryModal = candidateStore.isOpenVoteSummaryModal;

    useEffect(() => {
        const { classList } = document.body;

        if (isOpenVoteSummaryModal) {
            classList.add(BODY_OVERFLOW_HIDDEN_CLASS);
        } else {
            classList.remove(BODY_OVERFLOW_HIDDEN_CLASS);
        }

        return () => classList.remove(BODY_OVERFLOW_HIDDEN_CLASS);
    }, [isOpenVoteSummaryModal]);

    return (
        <Main
            coopId={coopId}
            customer={customer}
            slagList={slagList}
            heading={heading}
            description={description}
            isDisabledVote={isDisabledVote || isPreview}
            selectedElectionId={selectedElectionId}
            selectedSlagId={selectedSlagId}
            selectedRegionId={selectedRegionId}
            setSelectedSlagId={ballotStore.setSelectedSlagId}
            onSlagChange={ballotStore.onSlagChange}
            isUpdatingSlag={ballotStore.isUpdatingSlag}
            isShownCandidates={isShownCandidates}
            hideSlagList={isStandAdmin}
            voteStatus={voteStatus}
            isUserAlreadyVoted={candidateStore.isUserAlreadyVoted}
            isUserChild={candidateStore.isUserChild}
            isOpenVoteSummaryModal={isOpenVoteSummaryModal}
            electionLogoUrl={ballotStore.election?.electionLogoUrl || ''}
        />
    );
});

export default MainWithStore;
